import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

type Props = {
  nomeDoAcesso: string;
  /** Quantas pessoas usam o acesso escolhido — "atualizar" muda todas. */
  pessoas: number;
  mudouAcesso: boolean;
  mudouPermissoes: boolean;
  podeCriar: boolean;
  motivoSemCriar?: string;
  salvando: boolean;
  onAplicar: () => void;
  onAtualizar: () => void;
  onSalvarComoNovo: (nome: string) => void;
  onDescartar: () => void;
};

const botao =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-md px-3 text-[12.5px] font-semibold transition-colors disabled:opacity-60";

/** Os gestos de salvar do acesso. Só aparece quando há algo mexido. */
export function BarraDoAcesso(p: Props) {
  const [nomeando, setNomeando] = useState(false);
  const [nome, setNome] = useState("");
  const nadaPendente = !p.mudouAcesso && !p.mudouPermissoes;

  // Depois de salvar não sobra nada pendente: a barra volta ao começo, senão a próxima alteração
  // reabriria o campo de nome em vez dos botões.
  useEffect(() => {
    if (nadaPendente) {
      setNomeando(false);
      setNome("");
    }
  }, [nadaPendente]);

  if (nadaPendente) return null;

  if (nomeando && p.mudouPermissoes) {
    return (
      <form
        className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 p-2.5 shadow-lg dark:border-primary/30 dark:bg-card"
        onSubmit={(e) => {
          e.preventDefault();
          if (nome.trim().length >= 2) p.onSalvarComoNovo(nome.trim());
        }}
      >
        <input
          autoFocus
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Nome do novo acesso"
          maxLength={60}
          className="h-9 min-w-0 flex-1 rounded-md border border-zinc-200 bg-white px-2.5 text-sm dark:border-border dark:bg-card"
        />
        <button type="submit" disabled={p.salvando || nome.trim().length < 2} className={`${botao} bg-brand-600 text-white hover:bg-brand-700`}>
          {p.salvando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Criar e atribuir
        </button>
        <button type="button" onClick={() => setNomeando(false)} className={`${botao} text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted`}>
          Voltar
        </button>
      </form>
    );
  }

  return (
    <div className="sticky bottom-0 z-10 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 shadow-lg dark:border-amber-500/30 dark:bg-card">
      {p.mudouPermissoes && (
        <p className="text-[11.5px] text-amber-800 dark:text-amber-300">
          Atualizar muda o acesso de{" "}
          <strong>
            {p.pessoas} {p.pessoas === 1 ? "pessoa" : "pessoas"}
          </strong>{" "}
          que {p.pessoas === 1 ? "usa" : "usam"} "{p.nomeDoAcesso}".
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {p.mudouPermissoes ? (
          <>
            <button
              type="button"
              disabled={p.salvando || !p.podeCriar}
              title={p.podeCriar ? undefined : p.motivoSemCriar}
              onClick={() => setNomeando(true)}
              className={`${botao} bg-brand-600 text-white hover:bg-brand-700`}
            >
              Salvar como novo acesso
            </button>
            <button
              type="button"
              disabled={p.salvando}
              onClick={p.onAtualizar}
              className={`${botao} border border-zinc-200 bg-white text-zinc-800 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted`}
            >
              {p.salvando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Atualizar "{p.nomeDoAcesso}"
            </button>
          </>
        ) : (
          <button type="button" disabled={p.salvando} onClick={p.onAplicar} className={`${botao} bg-brand-600 text-white hover:bg-brand-700`}>
            {p.salvando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Aplicar "{p.nomeDoAcesso}"
          </button>
        )}
        <button type="button" disabled={p.salvando} onClick={p.onDescartar} className={`${botao} text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted`}>
          Descartar
        </button>
      </div>
      {p.mudouPermissoes && !p.podeCriar && p.motivoSemCriar && (
        <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{p.motivoSemCriar}</p>
      )}
    </div>
  );
}
